import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { VersionInfo } from '@tg/shared';
import { useFormat } from '@/i18n/useFormat';
import { cx } from '@/ui';
import { FRONT_BUILD, isStaleFront, useServerVersion } from './version';
import s from './version.module.css';

/** «01.10.2026 12:30» in the page language */
const DATE: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };

function useDate() {
  const f = useFormat();
  return (iso: string) => (Number.isNaN(Date.parse(iso)) ? iso : f.date(iso, DATE));
}

/**
 * Which build runs — a small line in the page footer: «Leyo 0.9.0 · build 1234 (abc1234) · 01.10.2026 12:30».
 *
 * Without it «deployed» and «updated» look the same: the screen is identical before and after a release. The
 * data is the SERVER's (what is installed), not the bundle's; the bundle's own commit only tells whether this
 * tab holds an old front — then «refresh the page». No silent state: loading, «development» (no build-info on
 * the server, the date is not invented) and «version not read» are all said aloud.
 */
export function VersionLine({ className }: { className?: string }) {
  const v = useServerVersion();
  const intl = useIntl();
  const date = useDate();

  let text: ReactNode;
  if (v.status === 'error') text = <FormattedMessage id="common.version.error" />;
  else if (v.status !== 'ok') text = <FormattedMessage id="common.version.loading" />;
  else text = describe(v.info, intl.formatMessage({ id: 'common.brand' }), (id, values) => intl.formatMessage({ id }, values), date);

  const stale = v.status === 'ok' && isStaleFront(v.info);
  return (
    <span className={cx(s.line, className)} data-testid="version-line">
      <span className={s.text}>{text}</span>
      {stale && (
        <button
          type="button"
          className={s.reload}
          title={intl.formatMessage(
            { id: 'common.version.staleHint' },
            { front: FRONT_BUILD.commit ?? '—', server: v.info.commit ?? '—' },
          )}
          onClick={() => window.location.reload()}
        >
          <FormattedMessage id="common.version.reload" />
        </button>
      )}
    </span>
  );
}

function describe(
  info: VersionInfo,
  brand: string,
  t: (id: string, values?: Record<string, string | number>) => string,
  date: (iso: string) => string,
): string {
  const parts = [`${brand} ${info.version}`];
  const commit = info.commit ? `(${info.commit})` : '';
  if (info.build !== null) parts.push([t('common.version.build', { build: info.build }), commit].filter(Boolean).join(' '));
  else if (commit) parts.push(commit);
  parts.push(info.builtAt ? date(info.builtAt) : t('common.version.dev'));
  return parts.join(' · ');
}

/** Profile → settings: both sides in full (server build, this bundle, environment). */
export function VersionDetails() {
  const v = useServerVersion();
  const date = useDate();
  const intl = useIntl();
  const dev = intl.formatMessage({ id: 'common.version.dev' });
  const row = (label: string, value: ReactNode) => (
    <div className={s.row}>
      <dt>
        <FormattedMessage id={label} />
      </dt>
      <dd>{value}</dd>
    </div>
  );
  const build = (b: { version: string; build: number | null; commit: string | null; builtAt: string | null }) =>
    [b.version, b.build !== null ? `#${b.build}` : null, b.commit, b.builtAt ? date(b.builtAt) : dev].filter(Boolean).join(' · ');

  return (
    <details className={s.details} data-testid="version-details">
      <summary>
        <FormattedMessage id="common.version.title" />
      </summary>
      <dl className={s.dl}>
        {row(
          'common.version.server',
          v.status === 'ok' ? build(v.info) : <FormattedMessage id={v.status === 'error' ? 'common.version.error' : 'common.version.loading'} />,
        )}
        {v.status === 'ok' && row('common.version.env', v.info.env)}
        {row('common.version.front', build(FRONT_BUILD))}
      </dl>
      {v.status === 'ok' && isStaleFront(v.info) && (
        <button type="button" className={s.reload} onClick={() => window.location.reload()}>
          <FormattedMessage id="common.version.reload" />
        </button>
      )}
    </details>
  );
}
